import { useState } from "react";

interface Lead {
  id: string;
  name: string;
  email: string;
  business: string;
  phone: string | null;
  source: string | null;
  email_sent: boolean;
  created_at: string;
}

interface Props {
  leads: Lead[];
  loading: boolean;
}

const PAGE_SIZE = 10;

function initials(name: string) {
  return name.split(" ").slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function LeadsTable({ leads, loading }: Props) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filtered = leads.filter(
    (l) =>
      l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.email.toLowerCase().includes(search.toLowerCase()) ||
      (l.business || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="bg-white border border-stone-100 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-stone-900">Leads registrados</h2>
          <p className="text-xs text-stone-400">{leads.length} registros en total</p>
        </div>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-stone-400 text-sm" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por nombre, email, negocio..."
            className="pl-9 pr-4 py-2 border border-stone-200 rounded-xl text-sm text-stone-700 placeholder-stone-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all w-72"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-stone-400">
          <i className="ri-loader-4-line animate-spin text-xl" />
          <span className="text-sm">Cargando leads...</span>
        </div>
      ) : paginated.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-stone-400">
          <div className="w-12 h-12 flex items-center justify-center">
            <i className="ri-user-search-line text-3xl" />
          </div>
          <p className="text-sm mt-2">{search ? "Sin resultados para esa búsqueda" : "Aún no hay leads registrados"}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Persona</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Negocio</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Teléfono</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Email</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Registro</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {paginated.map((lead) => (
                <tr key={lead.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-amber-700">{initials(lead.name)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-stone-800">{lead.name}</p>
                        <p className="text-xs text-stone-400">{lead.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-1 bg-stone-100 text-stone-700 text-xs rounded-lg font-medium">
                      {lead.business || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {lead.phone || <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    {lead.email_sent ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg font-medium">
                        <i className="ri-check-line" />
                        Enviado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 text-stone-500 text-xs rounded-lg font-medium">
                        <i className="ri-time-line" />
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-400">
                    {formatDate(lead.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <a
                        href={`mailto:${lead.email}`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
                        title="Enviar email"
                      >
                        <i className="ri-mail-send-line text-amber-600 text-sm" />
                      </a>
                      {lead.phone && (
                        <a
                          href={`tel:${lead.phone}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer"
                          title="Llamar"
                        >
                          <i className="ri-phone-line text-stone-500 text-sm" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-between">
          <p className="text-xs text-stone-400">
            Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone-200 disabled:opacity-40 hover:bg-stone-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-stone-600 text-sm" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  p === page
                    ? "bg-stone-900 text-white"
                    : "border border-stone-200 text-stone-600 hover:bg-stone-50"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone-200 disabled:opacity-40 hover:bg-stone-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-stone-600 text-sm" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
